import React from "react";
import {
  Container,
  Row,
  Button,
  Form,
  Col,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainOperationForm from "./components/MainOperationForm";
import SubOperationsPanel from "./components/SubOperationsPanel";
import ModalFornecedor from "./ModalFornecedor";
import { useOperationForm } from "./hooks/useOperationForm";

// AIDEV-NOTE: Main operations component - clean orchestration with modular components
const Operacoes = () => {
  const navigate = useNavigate();
  const {
    // Loading states
    loading,
    submitting,
    
    // Main form states and handlers
    cliente,
    direcao,
    moeda,
    cotacao,
    cotacaoFort,
    lucro,
    quantidade,
    ajustarPor,
    taxaFortPercentage,
    taxaRedeBrl,
    totalBrl,
    emitirNota,
    listaClientes,
    listaMoedas,
    listaFornecedores,
    usdBrl,
    
    // Sub-operations states and handlers
    subOperacoes,
    viewMode,
    buildFormData,
    searchTerm,
    availableSubOps,
    selectedSubOps,
    loadingSubOps,
    currentPage,
    totalPages,
    modalFornecedor,
    
    // Setters
    setCliente,
    setDirecao,
    setMoeda,
    setQuantidade,
    setAjustarPor,
    setTaxaFortPercentage,
    setTaxaRedeBrl,
    setTotalBrl,
    setEmitirNota,
    setSubOperacoes,
    setViewMode,
    setSearchTerm,
    setCurrentPage,
    setModalFornecedor,
    
    // Handlers
    handleBuildFormChange,
    handleBuildSubOperation,
    handleSelectSubOperation,
    handleAddSelectedSubOperations,
    handleSubmit,
    onFornecedorAdded,
    fetchAvailableSubOps,
    
    // Modal confirmation
    modalConfirmacao,
    toggleModalConfirmacao,
    handleConfirmLimparSubOperacoes,
    novaDirecao,
    novaMoeda,
    tipoMudanca
  } = useOperationForm();

  document.title = "Nova Operação | Painel Fort Exchange";

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
            <Spinner size="lg" />
            <span className="ms-2">Carregando dados...</span>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Nova Operação OTC" pageTitle="Aplicações" />
        
        {/* AIDEV-NOTE: ModalFornecedor moved outside Form to prevent nested form issue */}
        <ModalFornecedor
          isOpen={modalFornecedor}
          toggle={() => setModalFornecedor(!modalFornecedor)}
          onFornecedorAdded={onFornecedorAdded}
          listaClientes={listaClientes}
        />
        
        <Form onSubmit={handleSubmit}>
          <Row>
            {/* Formulário Principal - Col 8 */}
            <MainOperationForm
              // Form states
              cliente={cliente}
              direcao={direcao}
              moeda={moeda}
              cotacao={cotacao}
              cotacaoFort={cotacaoFort}
              lucro={lucro}
              quantidade={quantidade}
              ajustarPor={ajustarPor}
              taxaFortPercentage={taxaFortPercentage}
              taxaRedeBrl={taxaRedeBrl}
              totalBrl={totalBrl}
              emitirNota={emitirNota}
              
              // Data lists
              listaClientes={listaClientes}
              listaMoedas={listaMoedas}
              usdBrl={usdBrl}
              
              // Sub-operations
              subOperacoes={subOperacoes}
              
              // Setters
              setCliente={setCliente}
              setDirecao={setDirecao}
              setMoeda={setMoeda}
              setQuantidade={setQuantidade}
              setAjustarPor={setAjustarPor}
              setTaxaFortPercentage={setTaxaFortPercentage}
              setTaxaRedeBrl={setTaxaRedeBrl}
              setTotalBrl={setTotalBrl}
              setEmitirNota={setEmitirNota}
            />

            {/* Painel de Sub-operações - Col 4 */}
            <SubOperationsPanel
              // Sub-operations states
              subOperacoes={subOperacoes}
              viewMode={viewMode}
              setViewMode={setViewMode}
              setSubOperacoes={setSubOperacoes}
              
              // Current selected data
              cliente={cliente}
              moeda={moeda}
              direcao={direcao}
              
              // Data lists
              listaFornecedores={listaFornecedores}
              listaClientes={listaClientes}
              
              // Build form states
              buildFormData={buildFormData}
              handleBuildFormChange={handleBuildFormChange}
              handleBuildSubOperation={handleBuildSubOperation}
              
              // Search states
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              availableSubOps={availableSubOps}
              selectedSubOps={selectedSubOps}
              loadingSubOps={loadingSubOps}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              handleSelectSubOperation={handleSelectSubOperation}
              handleAddSelectedSubOperations={handleAddSelectedSubOperations}
              fetchAvailableSubOps={fetchAvailableSubOps}
              
              // Modal states
              modalFornecedor={modalFornecedor}
              setModalFornecedor={setModalFornecedor}
              onFornecedorAdded={onFornecedorAdded}
              
              // Modal confirmation
              modalConfirmacao={modalConfirmacao}
              toggleModalConfirmacao={toggleModalConfirmacao}
              handleConfirmLimparSubOperacoes={handleConfirmLimparSubOperacoes}
              novaDirecao={novaDirecao}
              novaMoeda={novaMoeda}
              tipoMudanca={tipoMudanca}
            />
          </Row>

          {/* Botões de ação */}
          <Row className="mt-4">
            <Col>
              <div className="d-flex justify-content-end gap-2">
                <Button 
                  type="button" 
                  color="secondary"
                  onClick={() => navigate("/operacoes")}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  color="primary"
                  disabled={submitting || !cliente || !moeda || subOperacoes.length === 0}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Criando Operação...
                    </>
                  ) : (
                    <>
                      <FeatherIcon icon="save" className="me-1" size={14} />
                      Criar Operação
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default Operacoes;
